/* eslint-disable react-refresh/only-export-components -- intentional: step exports a helper alongside the component */
import { useCallback, useState } from 'react';
import { Camera, Check, ImagePlus, Layers, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useGetMyAvatarsQuery, useGetTemplatesQuery } from '@/services/avatarApi';

import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
}

const MAX_PROMPT_CHARS = 500;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB (data URL grows ~33% in base64)

export function Step1Concept({ state, update, merge }: Props) {
  const { data: templates } = useGetTemplatesQuery();
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Only fetched when the user opts into "use existing". Avoids a query for
  // users coming in via the default fromPrompt path.
  const { data: myAvatars } = useGetMyAvatarsQuery(
    { limit: 30 },
    { skip: state.sourceMode !== 'fromExisting' },
  );

  const switchMode = useCallback((mode: 'fromPrompt' | 'fromExisting') => {
    if (mode === state.sourceMode) return;
    if (mode === 'fromExisting') {
      // Clear any prompt-flow artifacts and prepare to hold an avatar ref.
      merge({
        sourceMode: 'fromExisting',
        prompt: '',
        sourceImageDataUrl: undefined,
        sourceImageName: undefined,
        sourceImageType: undefined,
        templateId: undefined,
        // Don't clear avatarId — the user may have already picked one in
        // this mode previously and switched back.
      });
    } else {
      merge({
        sourceMode: 'fromPrompt',
        avatarId: undefined,
        avatarImageUrl: undefined,
        avatarHasAlpha: undefined,
        jobId: undefined,
      });
    }
  }, [state.sourceMode, merge]);

  const pickExisting = useCallback((id: string, imageUrl: string, hasAlpha: boolean, title?: string) => {
    merge({
      avatarId: id,
      avatarImageUrl: imageUrl,
      avatarHasAlpha: hasAlpha,
      title: title ?? state.title,
    });
  }, [merge, state.title]);

  const onPickPhoto = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError(`Photo too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      update('sourceImageDataUrl', reader.result as string);
      update('sourceImageName', file.name);
      update('sourceImageType', file.type);
      setPhotoError(null);
    };
    reader.onerror = () => setPhotoError('Could not read the photo.');
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [update]);

  const clearPhoto = useCallback(() => {
    update('sourceImageDataUrl', undefined);
    update('sourceImageName', undefined);
    update('sourceImageType', undefined);
    setPhotoError(null);
  }, [update]);

  const promptCount = state.prompt.length;

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">
          {state.sourceMode === 'fromExisting' ? 'Pick an existing avatar' : 'Start with an idea'}
        </h1>
        <p className="text-sm text-fg-muted">
          {state.sourceMode === 'fromExisting'
            ? 'Reuse a 2D character you already created. Skips image generation and goes straight to motion.'
            : 'Describe your character with words, drop in a reference photo, or both. Pick a style template to bias the output.'}
        </p>
      </header>

      {/* Source mode toggle — first thing the user sees */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => switchMode('fromPrompt')}
          className={cn(
            'flex flex-col items-start gap-1 rounded-xl border px-3 py-3 text-left transition-colors',
            state.sourceMode === 'fromPrompt'
              ? 'border-brand bg-brand/10 text-fg'
              : 'border-border bg-bg-secondary text-fg-muted',
          )}
        >
          <ImagePlus aria-hidden className="h-4 w-4" />
          <span className="text-xs font-bold text-fg">Create from prompt</span>
          <span className="text-[10px] text-fg-muted">Generates a new 2D image</span>
        </button>
        <button
          type="button"
          onClick={() => switchMode('fromExisting')}
          className={cn(
            'flex flex-col items-start gap-1 rounded-xl border px-3 py-3 text-left transition-colors',
            state.sourceMode === 'fromExisting'
              ? 'border-brand bg-brand/10 text-fg'
              : 'border-border bg-bg-secondary text-fg-muted',
          )}
        >
          <Layers aria-hidden className="h-4 w-4" />
          <span className="text-xs font-bold text-fg">Use existing avatar</span>
          <span className="text-[10px] text-fg-muted">Skip generation, jump to motion</span>
        </button>
      </div>

      {/* Branch: existing-avatar picker */}
      {state.sourceMode === 'fromExisting' && (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold uppercase text-fg-muted">Your avatars</label>
          {myAvatars && myAvatars.items.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {myAvatars.items.filter(a => a.imageUrl && a.status === 'active').map(a => {
                const isPicked = state.avatarId === a._id;
                return (
                  <button
                    key={a._id}
                    type="button"
                    onClick={() => pickExisting(a._id, a.imageUrl, !!a.hasAlpha, a.title)}
                    className={cn(
                      'relative aspect-square overflow-hidden rounded-xl border transition-opacity active:opacity-80',
                      isPicked ? 'border-brand ring-2 ring-brand' : 'border-border',
                    )}
                  >
                    <img src={a.thumbnailUrl ?? a.imageUrl} alt={a.title} className="h-full w-full object-cover" />
                    {isPicked && (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-brand-fg shadow">
                        <Check aria-hidden className="h-3 w-3" />
                      </span>
                    )}
                    <span className="absolute inset-x-0 bottom-0 line-clamp-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-3 text-[10px] font-semibold text-white">
                      {a.title || a.prompt.slice(0, 24)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="rounded-xl bg-bg-secondary p-3 text-xs text-fg-muted">
              You don't have any avatars yet. Switch to "Create from prompt" to make one.
            </p>
          )}
        </div>
      )}

      {/* Branch: from-prompt fields (hidden when reusing existing) */}
      {state.sourceMode === 'fromPrompt' && (
      <>
      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ed-title" className="text-xs font-semibold uppercase text-fg-muted">
          Title <span className="font-normal normal-case text-fg-muted/60">(optional)</span>
        </label>
        <input
          id="ed-title"
          type="text"
          value={state.title}
          onChange={e => update('title', e.target.value.slice(0, 80))}
          placeholder="My Clash Royale wizard"
          className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
        />
      </div>

      {/* Prompt */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ed-prompt" className="text-xs font-semibold uppercase text-fg-muted">
          Prompt
        </label>
        <textarea
          id="ed-prompt"
          value={state.prompt}
          onChange={e => update('prompt', e.target.value.slice(0, MAX_PROMPT_CHARS))}
          placeholder="A fierce dragon warrior with golden armor…"
          className="min-h-[120px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
        />
        <p className="text-right text-[11px] text-fg-muted">{promptCount} / {MAX_PROMPT_CHARS}</p>
      </div>

      {/* Photo */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">
          Reference photo <span className="font-normal normal-case text-fg-muted/60">(optional, locks model to Gemini)</span>
        </label>
        {state.sourceImageDataUrl ? (
          <div className="relative w-fit">
            <img
              src={state.sourceImageDataUrl}
              alt={state.sourceImageName ?? 'reference'}
              className="h-32 w-32 rounded-xl object-cover"
            />
            <button
              type="button"
              aria-label="Remove photo"
              onClick={clearPhoto}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-bg-secondary text-fg shadow"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-border bg-bg-secondary text-fg-muted transition-opacity active:opacity-80">
            <Camera aria-hidden className="h-6 w-6" />
            <input type="file" accept="image/*" onChange={onPickPhoto} className="hidden" />
          </label>
        )}
        {photoError && (
          <p className="text-xs text-red-400">{photoError}</p>
        )}
      </div>

      {/* Templates */}
      {templates && templates.length > 0 && (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold uppercase text-fg-muted">
            Style template <span className="font-normal normal-case text-fg-muted/60">(optional)</span>
          </label>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {templates.map(t => (
              <button
                key={t._id}
                type="button"
                onClick={() => update('templateId', state.templateId === t._id ? undefined : t._id)}
                className={cn(
                  'shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  state.templateId === t._id
                    ? 'border-brand bg-brand/10 text-fg'
                    : 'border-border bg-bg-secondary text-fg-muted',
                )}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </section>
  );
}

/** Validity rule for the Next button on Step 1.
 *  - fromPrompt: needs at least a prompt OR a reference photo.
 *  - fromExisting: needs an avatarId selected from the picker. */
export function step1Valid(state: WizardState): boolean {
  if (state.sourceMode === 'fromExisting') {
    return !!state.avatarId && !!state.avatarImageUrl;
  }
  return state.prompt.trim().length >= 3 || !!state.sourceImageDataUrl;
}
